import API from '@/api/path'
import { baseFetch } from '@/api/axiosBase'
import {
  dateUtcToLocalZone,
  getGtZerorDefault,
  getOrDefault
} from '@/utils/format'
import { downloadCSVData } from '@/utils/csvDownload'

const gasRegistrationModule = {
  namespaced: true,
  state: {
    gasRegistrationData: [],
    gasRegistrationYearMonthData: JSON.parse(localStorage.getItem('gasRegistrationYearMonthData')) || [], // 已登記的年度-月份
    gasRegistration: {},
    householdRegistationList: []
  },
  getters: {
    getGasRegistrationData: (state) => state.gasRegistrationData,
    getGasRegistrationYearMonthData: (state) => state.gasRegistrationYearMonthData,
    getGasRegistration: (state) => state.gasRegistration,
    getHouseholdRegistationList: (state) => state.householdRegistationList
  },
  mutations: {
    SET_GAS_REGISTRATION_DATA(state, payload) {
      state.gasRegistrationData = payload
      const yearMonthData = payload.map((x) => { return x?.yearMonth })
      state.gasRegistrationYearMonthData = yearMonthData
      localStorage.setItem('gasRegistrationYearMonthData', JSON.stringify(yearMonthData))
    },
    SET_GAS_REGISTRATION(state, payload) {
      const data = payload
      data.startTimeTimestamp = dateUtcToLocalZone(data.startTime, '')
      data.endTimeTimestamp = dateUtcToLocalZone(data.endTime, '')
      data.startTime = dateUtcToLocalZone(data.startTime, 'YYYY-MM-DD HH:mm:ss')
      data.endTime = dateUtcToLocalZone(data.endTime, 'YYYY-MM-DD HH:mm:ss')
      state.gasRegistration = data
    },
    SET_CURRENT_GAS_REGISTRATION(state, payload) {
      state.currentGasRegistation = payload
    },
    SET_HOUSEHOLD_REGISTRATION(state, payload) {
      const formatData = payload.map((it) => {
        it.previousRecord = getGtZerorDefault(it.previousRecord)
        it.currentRecord = getGtZerorDefault(it.currentRecord)
        it.currentUsage = getGtZerorDefault(it.currentUsage)
        it.registrantName = getOrDefault(it.registrantName)
        return it
      })
      state.householdRegistationList = formatData
    }
  },
  actions: {
    /**
     * 根據社區編號取得瓦斯登記列表
     * @param {string} communityId 社區ＩＤ
     */
    async GET_GAS_REGISTRATION_DATA({ commit }, payload) {
      const { communityId } = payload
      try {
        const res = await baseFetch(
          API.getGasRegistrationList.url(communityId),
          API.getGasRegistrationList.method
        )

        if (!res.isError && res.data) {
          commit('SET_GAS_REGISTRATION_DATA', res.data.data)
        }
      } catch (error) {
        console.error('error', error)
      }
    },
    /**
     * 根據瓦斯登記編號取得瓦斯登記詳細資訊
     * @param {string} gasRegistrationId 瓦斯登記ID
     */
    async GET_GAS_REGISTRATION({ commit }, payload) {
      const { gasRegistrationId } = payload
      try {
        const res = await baseFetch(
          API.getGasRegistration.url(gasRegistrationId),
          API.getGasRegistration.method
        )

        if (!res.isError && res.data) {
          commit('SET_GAS_REGISTRATION', res.data.data)
        }
      } catch (error) {
        console.error('error', error)
      }
    },
    /**
     * 新增（發佈）瓦斯登記項目
     * @param {Object} payload 包含了特定的設置或數據
     * @param {string} payload.gasRegistrationId 瓦斯登記ID，新增："" 發佈：ID
     * @param {string} payload.communityId 社區的ID
     * @param {string} payload.yearMonth 年月，例如 '2023-07'
     * @param {Number} payload.startTime 開始時間(TimeStamp)，例如 1690790400000
     * @param {Number} payload.endTime 結束時間(TimeStamp)，例如 1690790400000
     * @param {number} payload.status 狀態代碼，例如 2:待發佈, 3:已發佈
     * @returns
     */
    async ADD_GAS_REGISTRATION(_, payload) {
      try {
        const res = await baseFetch(
          API.addGasRegistration.url,
          API.addGasRegistration.method,
          payload
        )

        return res
      } catch (error) {
        console.error('error', error)
      }
    },
    /**
     * 根據瓦斯登記編號修改根據瓦斯登記
     * @param {Object} payload 包含了特定的設置或數據
     * @param {string} payload.gasRegistrationId 瓦斯登記ID，新增："" 發佈：ID
     * @param {string} payload.yearMonth 年月，例如 '2023-07'
     * @param {Number} payload.startTime 開始時間(TimeStamp)，例如 1690790400000
     * @param {Number} payload.endTime 結束時間(TimeStamp)，例如 1690790400000
     * @returns
     */
    async EDIT_GAS_REGISTRATION(_, payload) {
      const { gasRegistrationId, ...params } = payload

      try {
        const res = await baseFetch(
          API.editPaymentNotice.url(gasRegistrationId),
          API.editPaymentNotice.method,
          params
        )

        return res
      } catch (error) {
        console.error('error', error)
      }
    },
    /**
     * 根據瓦斯登記編號刪除瓦斯登記
     * @param {*} gasRegistrationId 瓦斯登記ＩＤ
     */
    async DELETE_GAS_REGISTRATION(_, payload) {
      try {
        const res = await baseFetch(
          API.deletePaymentNotice.url(payload),
          API.deletePaymentNotice.method
        )

        if (!res.isError && res.data) {
          return res
        }
      } catch (error) {
        console.error('error', error)
      }
    },
    /**
     * 發送瓦斯登記推播通知
     * @param {string} payload 包含了特定的設置或數據
     * @param {string} payload.gasRegistrationId 瓦斯登記ID
     * @param {string} payload.title 通知標題
     * @param {string} payload.content 通知內容
     * @param {Array} payload.householdIdList 通知戶別
     */
    async SEND_GAS_REGISTRATION_NOTIFICATION(_, payload) {
      try {
        const res = await baseFetch(
          API.sendGasRegistrationNotification.url,
          API.sendGasRegistrationNotification.method,
          payload
        )

        return res
      } catch (error) {
        console.error('error', error)
      }
    },
    /**
     * 戶別登記列表匯出CSV
     * @param {string} gasRegistrationId 瓦斯登記ID
     */
    async EXPORT_GAS_REGISTRATION_CSV(
      _,
      { gasRegistrationId, currentYearMonth }
    ) {
      try {
        const res = await baseFetch(
          API.getGasHouseholdRegistrationCSV.url(gasRegistrationId),
          API.getGasHouseholdRegistrationCSV.method
        )

        if (res.data) {
          const fileName = `${currentYearMonth}_瓦斯登記`
          downloadCSVData(res.data, fileName, 'csv')
          return res
        }
      } catch (error) {
        console.error('error', error)
      }
    },
    /**
     * 根據社區編號取得當期瓦斯登記
     * @param {string} currentCommunityId 社區ＩＤ
     */
    async GET_CURRENT_GAS_REGISTRATION({ commit }, payload) {
      try {
        const res = await baseFetch(
          API.getCurrentGasHouseholdRegistration.url(payload),
          API.getCurrentGasHouseholdRegistration.method
        )

        if (!res.isError && res.data) {
          // commit('SET_CURRENT_GAS_REGISTRATION', res.data.data)
          console.log(res.data.data)
        }
      } catch (error) {
        console.error('error', error)
      }
    },
    /**
     * 根據瓦斯登記編號取得瓦斯戶別登記列表
     * @param {string} gasRegistrationId  瓦斯登記ID
     */
    async GET_HOUSEHOLD_REGISTRATION({ commit }, payload) {
      const gasRegistrationId = payload
      try {
        const res = await baseFetch(
          API.getHouseholdRegistration.url(gasRegistrationId),
          API.getHouseholdRegistration.method
        )

        if (!res.isError && res.data) {
          commit('SET_HOUSEHOLD_REGISTRATION', res.data.data)
        }
      } catch (error) {
        console.error('error', error)
      }
    }
  }
}

export default gasRegistrationModule
